import type { VerificationQuestionView } from "@credit-acceptance/step6-bff-client";
import { type QueryClient, queryOptions } from "@tanstack/react-query";

import { workbenchIncomeVerificationApi } from "../../../../../api/client";
import { callScriptKeys } from "./callScriptKeys";
import { ALL_CALL_SCRIPT_QUESTION_TYPES, questionTypesForPerson } from "./callScriptQuestions";

export const CALL_SCRIPT_QUESTIONS_STALE_TIME = 60 * 60_000;

/**
 * Normalizes a `getVerificationQuestions` response into an array. The
 * generated client types this endpoint's response as a single
 * `VerificationQuestionView` (spec bug — its description says "Active
 * questions for the type", a catalog), but recap-service actually returns a
 * bare array. Handled defensively in case a genuinely singular response is
 * ever returned instead.
 * @param {unknown} data - The raw `getVerificationQuestions` response body.
 * @returns {VerificationQuestionView[]} The catalog entries, normalized to an array.
 */
function normalizeVerificationQuestions(data: unknown): VerificationQuestionView[] {
  if (Array.isArray(data)) {
    return data;
  }
  return data && typeof data === "object" ? [data as VerificationQuestionView] : [];
}

/**
 * Shared queryOptions for the Call Script verification question catalog
 * (key + queryFn). Backed by
 * WorkbenchIncomeVerificationApi.getVerificationQuestions, a global lookup
 * keyed only by `type` (no approvalId/applicantId).
 *
 * The catalog is global reference data that doesn't change during a
 * session, so it's held fresh for an hour and kept in the cache well
 * beyond that so a persisted (localStorage) copy survives a reload.
 *
 * Disabled until `type` is resolved and the caller opts in — only while the
 * Call Script modal is open.
 * @param {string} type - The verification-question type code (e.g. `EVIDQ1`).
 * @param {boolean} enabled - Caller opt-in; combined with the presence guard.
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function callScriptQuestionsQuery(type: string, enabled = true) {
  return queryOptions({
    queryKey: callScriptKeys.questions(type),
    queryFn: async ({ signal }): Promise<VerificationQuestionView[]> => {
      const response = await workbenchIncomeVerificationApi.getVerificationQuestions(type, { signal });
      return normalizeVerificationQuestions(response.data);
    },
    staleTime: CALL_SCRIPT_QUESTIONS_STALE_TIME,
    gcTime: 24 * 60 * 60_000,
    retry: false,
    enabled: enabled && !!type,
  });
}

/**
 * Prefetches the verification question catalog for every type code the Call
 * Script modal can request (all income slots, both roles), so opening the
 * modal for any slot is served from cache. Skips types already cached and
 * fresh. Fire-and-forget: failures are left for the per-type query to retry
 * and surface when that type is actually opened.
 * @param {QueryClient} queryClient - The app's TanStack Query client.
 * @returns {void}
 */
export function prefetchAllCallScriptQuestions(queryClient: QueryClient): void {
  for (const type of ALL_CALL_SCRIPT_QUESTION_TYPES) {
    void queryClient.prefetchQuery(callScriptQuestionsQuery(type));
  }
}

/**
 * Resolves a role's merged question catalog (all 3 income-slot types),
 * fetching any type not yet cached instead of trusting that the modal-open
 * prefetch already finished. `getCallScriptQuestionsForPerson` alone reads
 * the cache synchronously, which races the fire-and-forget
 * {@link prefetchAllCallScriptQuestions} call fired on modal open — if Save
 * is clicked before that prefetch resolves (slow network, or a fast
 * analyst), the cache is still empty and every field's
 * `verificationQuestionPk` lookup fails, silently producing an empty
 * `verificationAnswers[]` in the save payload. Called at Save time so the
 * catalog is guaranteed to be loaded before the payload is built.
 * @param {QueryClient} queryClient - The app's TanStack Query client.
 * @param {"driver" | "secondSigner"} personPath - Which applicant.
 * @returns {Promise<VerificationQuestionView[]>} The merged catalog for the role.
 */
export async function ensureCallScriptQuestionsForPerson(
  queryClient: QueryClient,
  personPath: "driver" | "secondSigner"
): Promise<VerificationQuestionView[]> {
  const results = await Promise.all(
    questionTypesForPerson(personPath).map((type) => queryClient.ensureQueryData(callScriptQuestionsQuery(type)))
  );
  return results.flat();
}
